/**
 * #11a — the "Wrap in a single transaction" tooltip on the Schema Compare
 * results toolbar, per destination engine.
 *
 * It used to be one hardcoded string on `SchemaCompareToolbar`'s `<Label>`,
 * describing `SET XACT_ABORT ON` + `BEGIN TRANSACTION`, the per-batch guard,
 * and the syntax-error hole. All three are T-SQL facts, and #98's follow-up
 * made the checkbox visible on PostgreSQL too — where the mechanism is
 * different and the syntax-error exception does not exist at all. A PostgreSQL
 * user reading the SQL Server text would be told their script carries a hole it
 * does not have, next to machinery it does not use.
 *
 * Kept in its own module rather than on the component so the toolbar file does
 * not export a non-component (the repo lints `react-refresh/only-export-
 * components` at `--max-warnings 0`), and so the wording is unit-testable
 * without a render.
 *
 * The wording tracks the engines' `-- Atomicity:` script headers in
 * `electron/schema-compare/transactionWrap.ts` — same two claims, said to the
 * user before generation instead of inside the artefact. The TEXT is pinned
 * separately on each side rather than shared, because the two say the same
 * thing in different registers (a tooltip before the fact, a comment inside the
 * artefact) and forcing one string on both would make each worse.
 *
 * What IS shared is the engine list the two agree about: `WRAP_CAPABLE_ENGINES`
 * (`./wrapCapableEngines`), which the main process imports for the MCP
 * generator's default and for whether the unwrapped header points at this
 * control at all. An earlier draft of this comment said "nothing in this
 * feature imports across the renderer/electron boundary at run time" — #11a
 * made that false, and the reason it had to is exactly the defect that change
 * closed: the UI and the MCP tool held separate copies of that list and
 * disagreed about PostgreSQL.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

/**
 * The trade-off half of the copy, identical on every engine: an all-or-nothing
 * script is not free, and a user who wants partial progress has to be told the
 * box is what takes it away. Copy that only sells the upside would hide it.
 */
const UNTICK_REASON =
  ' Untick it to let a very large sync commit as it goes, or to keep whatever ' +
  'progress a failed run had already made.';

/**
 * `undefined` is NOT a "some other engine" case — the control is only rendered
 * for the wrap-capable engines. It is what a caller that has not passed
 * `engine` gets, and it deliberately returns the ENGINE-NEUTRAL text, true on
 * both engines that render the box, rather than defaulting to SQL Server's:
 * defaulting to SQL Server's is exactly how a PostgreSQL user ends up reading
 * T-SQL again the day a caller forgets the prop.
 */
export function wrapInTransactionTooltip(engine: DatabaseEngineId | undefined): string {
  if (engine === 'mssql') {
    return (
      'On by default. Roll back on first run-time error: wraps the whole script in ' +
      'SET XACT_ABORT ON + BEGIN TRANSACTION, guards every batch, and commits only if every ' +
      'run-time step succeeded — instead of leaving the target half-migrated. A syntax error is ' +
      "the one case this can't catch; see the script header." +
      UNTICK_REASON
    );
  }
  if (engine === 'pgsql') {
    return (
      'On by default. Roll back on first error: wraps the whole script in BEGIN; … COMMIT;. ' +
      'PostgreSQL DDL is transactional, so a failure anywhere rolls the whole script back — in ' +
      'psql and here alike — instead of leaving the target half-migrated. No syntax-error ' +
      'exception: any error ends the transaction.' +
      UNTICK_REASON
    );
  }
  return (
    'On by default. Roll back on first error: wraps the whole script in a single transaction and ' +
    'commits only if every step succeeded — instead of leaving the target half-migrated. The ' +
    'script header states exactly what that covers on this engine.' +
    UNTICK_REASON
  );
}
